<template>
  <div class="psui-el-draggable">
    <div
      class="psui-el-draggable-wrapper"
      v-for="(columnGroup, indexColumnGroup) in getColumns.columnGroups"
      :key="indexColumnGroup"
      :ref="indexColumnGroup"
      :id="`edit-group-${indexColumnGroup}`"
    >
      <PsTooltip
        position="top"
        :ignore-dialog="!columnGroup.disabled"
        class="w-full"
      >
        <template #trigger>
          <div
            class="psui-el-draggable-wrapper-title"
            :class="{ 'psui-opacity-50 psui-pointer-events-none': columnGroup.disabled }"
            draggable="true"
            tabindex="0"
            role="button"
            :aria-label="`Group: ${columnGroup.title}. Use Up/Down arrows to reorder this group.`"
            @keydown.up.prevent="moveGroup(indexColumnGroup, -1)"
            @keydown.down.prevent="moveGroup(indexColumnGroup, 1)"
            @dragstart="emit('drag-start-group', indexColumnGroup)"
            @dragend="emit('drag-end-group', indexColumnGroup)"
            @dragover="emit('drag-over-group', indexColumnGroup)"
          >
            <span class="flex psui-items-center">
              <p>{{ columnGroup.title }}</p>
              <PsIcon
                class="psui-el-draggable-wrapper-title-icon"
                v-if="columnGroup.hasHelper && columnGroup.hasHelper.showOnEditHideColumns"
                icon="info"
                size="18"
                :icon-classes="columnGroup.disabled ? 'psui-ml-2 psui-text-gray-60' : 'psui-ml-2 psui-text-blue-50 psui-cursor-pointer'"
                @click="emit('column-group-helper', columnGroup.hasHelper)"
              />
            </span>
            <i class="material-icons-round">drag_indicator</i>
          </div>

          <div
            class="psui-el-draggable-wrapper-list"
            :class="{ 'psui-opacity-50 psui-pointer-events-none': columnGroup.disabled }"
          >
            <div
              v-for="(column, indexColumn) in columnGroup.columns"
              :key="`edit-columns-column-${indexColumnGroup}-${indexColumn}`"
              :id="`edit-columns-column-${indexColumnGroup}-${indexColumn}`"
              class="psui-el-draggable-item"
              draggable="true"
              tabindex="0"
              role="listitem"
              :aria-label="`Column: ${column.title}. Use Up/Down arrows to reorder within the group.`"
              @keydown.up.prevent="moveItem(indexColumnGroup, indexColumn, -1)"
              @keydown.down.prevent="moveItem(indexColumnGroup, indexColumn, 1)"
              @dragstart="emit('drag-start-item', { indexColumnGroup, indexColumn }, $event)"
              @dragover="emit('drag-over-item', { indexColumnGroup, indexColumn }, $event)"
              @dragend="emit('drag-end-item', $event)"
              v-tooltip="column.tooltip || undefined"
            >
              <div class="psui-w-full psui-flex psui-items-center">
                <span class="psui-el-draggable-item-title psui-flex psui-flex-row psui-items-center">
                  <PsCheckboxSimple
                    size="small"
                    :label="column.title"
                    :class="{ active: column.isActive }"
                    :checked="column.isActive"
                    :disabled="column.isDisabled"
                    class="psui-el-draggable-item-checkbox"
                    @change="emit('on-select-item', { studyKey: getColumns.key, indexColumnGroup, indexColumn, columnGroupKey: columnGroup.key })"
                    @keydown.enter.native="emit('on-select-item', { studyKey: getColumns.key, indexColumnGroup, indexColumn, columnGroupKey: columnGroup.key })"
                  />
                  <PsIcon
                    class="psui-el-draggable-item-title-icon"
                    v-if="column.hasHelper && column.hasHelper.showOnEditHideColumns"
                    icon="info"
                    size="18"
                    :icon-classes="columnGroup.disabled ? 'psui-ml-2 psui-text-gray-60' : 'psui-ml-2 psui-text-blue-50 psui-cursor-pointer'"
                    @click="emit('column-helper', column.hasHelper)"
                  />
                </span>
                <span class="psui-el-draggable-item-append">
                  <i class="psui-icon">drag_indicator</i>
                </span>
              </div>
            </div>
          </div>
        </template>
        <template #content>
          {{ columnGroup.disabledTitle }}
        </template>
      </PsTooltip>
    </div>
  </div>
</template>

<script setup>
import { nextTick } from 'vue'

const emit = defineEmits([
  'drag-start-group',
  'drag-end-group',
  'drag-over-group',
  'drag-start-item',
  'drag-over-item',
  'drag-end-item',
  'column-helper',
  'on-select-item',
  'column-group-helper',
  'reorder-group',
  'reorder-item', 
])

const props = defineProps({
  /**
   * It sets the data that will be used.
   */
  getColumns: {
    type: Object,
    default: () => ({})
  },
  /**
   * It sets the module which will be used to set getters and actions.
   */
  module: {
    type: String,
    default: '',
  },

  /**
   * It disables the input. All mouse events are disabled.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
})

const moveGroup = (index, direction) => {
  const targetIndex = index + direction
  const groups = props.getColumns.columnGroups

  if (targetIndex >= 0 && targetIndex < groups.length) {
    emit('reorder-group', { from: index, to: targetIndex })
    
    nextTick(() => {
      const el = document.querySelector(`#edit-group-${targetIndex} .psui-el-draggable-wrapper-title`)
      el?.focus()
    })
  }
}

const moveItem = (groupIndex, itemIndex, direction) => {
  const columns = props.getColumns.columnGroups[groupIndex].columns
  const targetIndex = itemIndex + direction

  if (targetIndex >= 0 && targetIndex < columns.length) {
    emit('reorder-item', { groupIndex, from: itemIndex, to: targetIndex })

    nextTick(() => {
      const el = document.getElementById(`edit-columns-column-${groupIndex}-${targetIndex}`)
      el?.focus()
    })
  }
}
</script>
